import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import * as ChartModel from "./chart-plotter.model";
import { catchError } from "rxjs/operators";
import { ABS_PERIOD_TYPE } from "@va/core-library/period-filter";
@Injectable({
  providedIn: "root",
})
export class ChartPlotterDataService {
  apiEP: string = window.apiUrl;

  constructor(private http: HttpClient) {}

  getChartsAverageData(payload): Observable<any> {
    return this.http
      .post(`${this.apiEP}/comps/get_top_tickers`, payload)
      .pipe(catchError((error) => of(error)));
  }

  getChartsData(params): Observable<any> {
    let viewBy = "";
    let revision = "";
    if (params.chartTypeId === "3") {
      revision = `&revision=${params.revision}`;
    } else if (params.chartTypeId === "1") {
      viewBy = `&viewBy=${params.dataView}`;
    }
    const period =
      params.periods && params.periods.length
        ? `&period=${params.periods.join("&period=")}`
        : "";
    const otherPeriod =
      params.otherPeriods && params.otherPeriods.length
        ? `&other_period=${params.otherPeriods.join("&other_period=")}`
        : "";
    const periodType = `&period_type=${
      params.periodType || ABS_PERIOD_TYPE.dataId
    }`;
    let paramLink = `chart_type_id=${
      params.chartTypeId
    }&line_id=${params.lines.join(
      "&line_id=",
    )}${revision}${period}${otherPeriod}${periodType}${viewBy}`;
    if (params.currency && params.currency !== "Base") {
      paramLink += `&currency=${params.currency}`;
    }
    if (params.ticker) {
      paramLink += `&ticker=${params.ticker}`;
    }
    return this.http
      .get(`${this.apiEP}/charts_page/get_charts_data?${paramLink}`)
      .pipe(catchError((error) => of(error)));
  }

  getChartsMeta(params): Observable<any> {
    let paramLink = "";
    if (params.lines && params.lines.length) {
      paramLink += `line_ids=${params.lines.join("&line_ids=")}`;
    }
    if (params.tickers && params.tickers.length) {
      paramLink += `&ticker=${params.tickers.join("&ticker=")}`;
    }
    if (params.currency && params.currency !== "Base") {
      paramLink += `&currency=${params.currency}`;
    }
    return this.http.get(
      `${this.apiEP}/charts_page/get_charts_meta?${paramLink}`,
    );
  }

  getTotalAvgRevisionChartData(params): Observable<any> {
    return this.http.post(
      `${this.apiEP}/charts_page/get_agg_revision_chart_data`,
      params,
    );
  }

  getMultiCompanyRevisionChartData(params): Observable<any> {
    const paramLink = `periodstart=${
      params.period
    }&ticker=${params.tickers.join("&ticker=")}&dapparamid=${
      params.dapparamid
    }&revdatatype=1&cut=STD&current_quarter=${params.current_quarter}&source=${
      params.source
    }&percentage_change=${params.percentChange}`;
    return this.http.get(`${this.apiEP}api/chartrevisiondata?${paramLink}`);
  }
  getMultiCompanyBarChartData(params): Observable<any> {
    const paramLink = `industry_id=${
      params.industryId
    }&ticker=${params.tickers.join(
      "&ticker=",
    )}&chart_type=bar&currency=USD&period=${params.period}&y=${
      params.yAxisLine
    }&relative_period=${params.relativePeriod}`;
    return this.http.get(
      `${this.apiEP}/industry/get_bubble_chart_data?${paramLink}`,
    );
  }
  getMultiCompanyBubbleChartData(params): Observable<any> {
    const paramLink = `industry_id=${
      params.industryId
    }&ticker=${params.tickers.join("&ticker=")}&x=${params.xAxisLine}&y=${
      params.yAxisLine
    }&z=${params.zAxisLine}&chart_type=bubble&currency=USD&period=${
      params.period
    }&relative_period=${params.relativePeriod}`;
    return this.http.get(
      `${this.apiEP}/industry/get_bubble_chart_data?${paramLink}`,
    );
  }

  getMiniRevisionChartData(
    params,
  ): Observable<ChartModel.ChartMiniRevisionDataResponse> {
    let periodUrl = "";
    if (params.period) {
      periodUrl += `&periodstart=${params.period}`;
    }
    if (params.otherPeriod && params.otherPeriod.length) {
      periodUrl += `&other_period=${params.otherPeriod}`;
    }
    periodUrl += `&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    const paramLink = `&ticker=${params.ticker}${periodUrl}&dapparamid=${params.dapId}&depth=implied&source=CONSENSUS&revdatatype=1&monitor=true&current_quarter=${params.currentQuater}`;
    return this.http.get<ChartModel.ChartMiniRevisionDataResponse>(
      `${this.apiEP}/api/chartrevisiondata?${paramLink}`,
    );
  }

  getMiniTrendChartData(
    params,
  ): Observable<ChartModel.ChartMiniTrendDataResponse> {
    let periodUrl = "";
    if (params.period) {
      periodUrl += `&period=${params.period}`;
    }
    if (params.otherPeriod && params.otherPeriod.length) {
      periodUrl += `&other_period=${params.otherPeriod}`;
    }
    periodUrl += `&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    const paramLink = `ticker=${params.ticker}${periodUrl}&depth=${params.gAdjust}&dapparameterid=${params.dapId}&current_quarter=${params.currentQuater}&chart_type=trend`;
    return this.http.get<ChartModel.ChartMiniTrendDataResponse>(
      `${this.apiEP}/api/get_data_for_graphical_view?${paramLink}`,
    );
  }

  getDistributionAnalysisChartData(
    params,
  ): Observable<ChartModel.ChartDistAnalysisDataResponse> {
    const url = `api/distribution_chart_waterfall`;
    let paramLink = `ticker=${params.ticker}&period=${params.period}&dapparameterid=${params.dapparamId}&cut=${params.cut}`;
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    return this.http.get<ChartModel.ChartDistAnalysisDataResponse>(
      `${this.apiEP}/${url}?${paramLink}`,
    );
  }

  getDistributionRangeChartData(
    params,
  ): Observable<ChartModel.ChartDistTrendDataResponse> {
    const url = `api/get_data_for_graphical_view`;
    let periodUrl = "";
    if (params.period) {
      periodUrl += `&period=${params.period}`;
    }
    if (params.otherPeriod && params.otherPeriod.length) {
      periodUrl += `&other_period=${params.otherPeriod}`;
    }
    periodUrl += `&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    let paramLink = `ticker=${params.ticker}${periodUrl}&dapparameterid=${params.dapparamId}&cut=${params.cut}&source=Consensus&chart_type=distribution`;
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    if (params.current_quarter && params.current_quarter !== "")
      paramLink += `&current_quarter=${params.current_quarter}`;
    return this.http.get<ChartModel.ChartDistTrendDataResponse>(
      `${this.apiEP}/${url}?${paramLink}`,
    );
  }

  getTrendBarChartData(
    params,
  ): Observable<ChartModel.ChartDistTrendDataResponse> {
    const url = `api/get_data_for_graphical_view`;
    let periodUrl = "";
    if (params.period) {
      periodUrl += `&period=${params.period}`;
    }
    if (params.otherPeriod && params.otherPeriod.length) {
      periodUrl += `&other_period=${params.otherPeriod}`;
    }
    periodUrl += `&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    let paramLink = `ticker=${params.ticker}${periodUrl}&dapparameterid=${params.dapparamId}&cut=${params.cut}&source=${params.source}&chart_type=trend`;
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    return this.http.get<ChartModel.ChartDistTrendDataResponse>(
      `${this.apiEP}/${url}?${paramLink}`,
    );
  }

  getRevisionAnalysisChartData(
    params,
  ): Observable<ChartModel.ChartRevisionAnalysisDataResponse> {
    const url = `api/chartsrevisionanalysis`;
    let periodUrl = "";
    if (params.period) {
      periodUrl += `&periodstart=${params.period}`;
    }
    if (params.otherPeriod && params.otherPeriod.length) {
      periodUrl += `&other_period=${params.otherPeriod}`;
    }
    periodUrl += `&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    let paramLink = `dapparamid=${params.dapparamId}&cut=${params.cut}${periodUrl}&source=${params.source}&ticker=${params.ticker}`;
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    return this.http.get<ChartModel.ChartRevisionAnalysisDataResponse>(
      `${this.apiEP}/${url}?${paramLink}`,
    );
  }

  getRevisionChartData(
    params,
  ): Observable<ChartModel.ChartRevisionDataResponse> {
    const url = `api/chartrevisiondata`;
    let periodUrl = "";
    if (params.period) {
      periodUrl += `&periodstart=${params.period}`;
    }
    if (params.otherPeriod && params.otherPeriod.length) {
      periodUrl += `&other_period=${params.otherPeriod}`;
    }
    periodUrl += `&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    let paramLink = `dapparamid=${params.dapparamId}${periodUrl}&revdatatype=${params.revisionVal}
&cut=${params.cut}`;
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    if (
      params.isRevisionPeers === true &&
      params.source !== "VA Actuals/Consensus"
    ) {
      paramLink += `&source=${params.source}`;
      if (params.tickerList && params.tickerList.length > 0) {
        paramLink += `&ticker=${params.tickerList.join(
          "&ticker=",
        )}&current_ticker=${params.ticker}`;
      } else {
        paramLink += `&current_ticker=${params.ticker}`;
      }
    } else if (
      params.selectedRevisionSources &&
      params.selectedRevisionSources.length > 0
    ) {
      const filteredselectedRevisionSources =
        params.selectedRevisionSources.filter(
          (d) => d !== "VA Actuals/Consensus",
        );
      paramLink += `&source=${filteredselectedRevisionSources.join(
        "&source=",
      )}&ticker=${params.ticker}`;
    } else {
      paramLink += `&source=${params.source}&ticker=${params.ticker}`;
    }
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    return this.http.get<ChartModel.ChartRevisionDataResponse>(
      `${this.apiEP}/${url}?${paramLink}`,
    );
  }
  getSurpriseAnalysisChartData(params): Observable<any> {
    const url = `api/surprise_chart`;
    let paramLink = `type=1&ticker=${params.ticker}&period=${
      params.period
    }&dapparameterid=${params.dapparamId}&cut=${
      params.cut
    }&source=${params.selectedSurpriseSources.join("&source=")}`;
    if (params.current_quarter && params.current_quarter !== "")
      paramLink += `&current_quarter=${params.current_quarter}`;
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    return this.http.get(`${this.apiEP}/${url}?${paramLink}`);
  }
  getSurpriseLineChartData(params): Observable<any> {
    const url = `api/surprise_chart`;
    let paramLink = `type=0&ticker=${params.ticker}&period=${
      params.period
    }&dapparameterid=${params.dapparamId}&cut=${
      params.cut
    }&source=${params.selectedSurpriseSources.join("&source=")}`;
    if (params.current_quarter && params.current_quarter !== "")
      paramLink += `&current_quarter=${params.current_quarter}`;
    if (
      params.selectedCurrency &&
      params.selectedCurrency !== "Base" &&
      (params.cut === "STD" || params.cut === "NMV")
    ) {
      paramLink += `&currency=${params.selectedCurrency}`;
    }
    if (params.cut !== "STD") {
      paramLink += `&depth=${params.gAdjust}`;
    }
    return this.http.get(`${this.apiEP}/${url}?${paramLink}`);
  }
  getFreshnessData(
    ticker: string,
    year: number,
  ): Observable<ChartModel.FreshnessDataResponse> {
    return this.http.get<ChartModel.FreshnessDataResponse>(
      `${this.apiEP}/api/dashboarddata/${ticker}?year=${year}`,
    );
  }
  getForecastData(ticker: string): Observable<ChartModel.ForecastDataResponse> {
    return this.http.get<ChartModel.ForecastDataResponse>(
      `${this.apiEP}/api/forecastdata/${ticker}`,
    );
  }

  /**
   * It fetches the stock price revision data from api
   * @param {number} params - Absolute year in number
   * @returns {StockPriceRevisionResponse} response of stock price
   */
  getStockPriceChartData(
    params,
  ): Observable<ChartModel.StockPriceRevisionResponse> {
    const paramLink = `ticker=${params.ticker}&year=${params.year}&type=${
      params.type
    }&period_start_date=${params.periodStartDate}&period_end_date=${
      params.periodEndDate
    }&period_type=${params.periodType || ABS_PERIOD_TYPE.dataId}`;
    return this.http.get<any>(
      `${this.apiEP}/charts_page/get_stock_price_chart_data?${paramLink}`,
    );
  }
}
